import { notEmpty } from '@futrli/shared-services';

import { CATEGORY_NAME_INDEXES } from '../../../../../core/constants';
import {
  ConsolidatedForecastReportTab,
  ConsolidatedForecastReportStructure,
  ConsolidatedForecastReportTabChild
} from '../../../../../core/types';
import { ForecastTree, UKForecastLayoutItem, UKForecastLayoutItemDisplayType } from '../../types';

/**
 * Returns a flat layout, where all children listed for this tab will appear one
 * after the other on the same grid
 * @param trees - The forecast trees for each datasource
 * @param tab - The current tab configuration
 * @param selectedCategoryId - The ID of the selected category
 */
export const getFlatLayout = (
  trees: Record<string, ForecastTree>,
  tab: ConsolidatedForecastReportTab,
  isCashFlow: boolean
): UKForecastLayoutItem[] => {
  return tab.children
    .map(child => {
      const node = getTabChildNode(trees, child, isCashFlow);

      if (node) {
        return {
          category: CATEGORY_NAME_INDEXES.income,
          datasource: isCashFlow ? 'cash_flow' : (child.find_in as keyof ConsolidatedForecastReportStructure),
          displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
          nodes: [node]
        };
      }
    })
    .filter(notEmpty);
};

/**
 * Returns a select-one layout, where the selected node will be shown on the grid.
 * You should supply categoryOptions to your grid when using this layout so that the
 * drop down list for switching categories will be shown
 * @param trees - The forecast trees for each datasource
 * @param tab - The current tab configuration
 * @param selectedCategoryId - The ID of the selected category
 */
export const getSelectOneLayout = (
  trees: Record<string, ForecastTree>,
  tab: ConsolidatedForecastReportTab,
  selectedCategoryId: CATEGORY_NAME_INDEXES,
  isCashFlow: boolean
): UKForecastLayoutItem[] => {
  const childNodes = tab.children.map(child => trees[child.find_in]?.index[child.ref]).filter(notEmpty);
  const currentChildNode = childNodes.find(child => child.static_id === selectedCategoryId);
  const currentTabChild = currentChildNode && tab.children.find(child => child.ref === currentChildNode.ref);

  if (currentTabChild) {
    const dataChildNode = getTabChildNode(trees, currentTabChild, isCashFlow);

    if (dataChildNode) {
      return [
        {
          category: selectedCategoryId,
          datasource: isCashFlow ? 'cash_flow' : (currentTabChild.find_in as keyof ConsolidatedForecastReportStructure),
          displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
          nodes: dataChildNode.children,
          totalNode: dataChildNode, // specifying totalNode separately returns the totalRow separately
          options: {
            startingLevel: 1
          }
        }
      ];
    }
  }

  return [];
};

/**
 * Returns the node for the given tab child. When in cash flow view, we have to do
 * a bit of extra work to find the equivalent cash flow node, which this function handles.
 * @param trees - All combined forecast tree data
 * @param tabChild - The child of the tab you wish to get the node for
 * @param isCashFlow - Wether or not we are in cash flow view
 */
export const getTabChildNode = (
  trees: Record<string, ForecastTree>,
  tabChild: ConsolidatedForecastReportTabChild,
  isCashFlow: boolean
) => {
  const datasource = tabChild.find_in as keyof ConsolidatedForecastReportStructure;
  const index = trees[datasource].index;
  const node = index[tabChild.ref];

  // Bit of a hack here to find the cash flow equivalent node if we are in cash flow view
  if (node && isCashFlow) {
    return Object.values(trees.cash_flow.index).find(n => n.type === node.type && n.static_id === node.static_id);
  }

  return node;
};
